import { FunctionComponent, useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import classnames from 'classnames';

import { Locales } from 'types/locale';
import { Routes } from 'types/routes';

import { imageResizer } from 'utils/image';
import { select as selectLocale } from 'utils/locale';
import { prettierPhoneNumber, withVariables } from 'utils/string';

import UserAgentContext from 'components/context/UserAgent';
import Button, { ButtonElements, ButtonIconPositions, ButtonIconTypes, ButtonLayouts, ButtonVariants } from 'components/layout/Button';
import SaveButton from 'components/modules/public/layout/controls/SaveButton';
import ShareButton from 'components/modules/public/layout/controls/ShareButton';

import { Props } from './index';
import StyledComponent from './styles';

const PublicLayoutJobsMediumJobBox: FunctionComponent<Props> = ({
    job,
    jobCity,
    buttonProps,
    onApply,
    applicationButtonVisible,
}) => {
    const [isMobileUserAgent, setIsMobileUserAgent]: [boolean, Function] = useState(null);

    const router = useRouter();
    const context = useContext(UserAgentContext);

    useEffect(() => {
        setIsMobileUserAgent(context);
    }, [context]);

    const translations = selectLocale({
        [Locales.Pl]: require('./locales/pl.json'),
    });

    const getButtonProps = () => {
        if(job?.isCvRequired) return { ...buttonProps?.apply };
        if(!job?.isCvRequired) return { ...buttonProps?.applyCvFree };
    };
    const [isBtnActice, setIsBtnActive] :[ boolean, Function] = useState(false);
    const  handleClick = () => {
        setTimeout(()=>{
            setIsBtnActive(true);
        }, 200);
    };

    return (
        <StyledComponent
            className="public-layout-jobs-medium-job-box"
        >
            {isMobileUserAgent ? (
                <div className="mobile-box">
                    <div className="content-container">
                        <div className="content-header">
                            <div
                                className="title title-job-name"
                                dangerouslySetInnerHTML={{ __html: job?.name }}
                            />
                            { job?.jobSalaries && job?.jobSalaries.map((salary, idx) =>
                            {return translations?.[salary.name]?.label ?
                                (
                                    <div
                                        key={salary.id}
                                        className={`salary salary-${idx}`}
                                        dangerouslySetInnerHTML={{ __html: `${translations?.[salary.name]?.label}: ${salary.salaryFrom} - ${salary.salaryTo} ${translations?.[salary.name]?.month}` }}
                                    />
                                ) : salary.salaryFrom > 0 && (
                                    <div
                                        key={salary.id}
                                        className={`salary salary-${idx}`}
                                        dangerouslySetInnerHTML={{ __html: `${salary.salaryFrom} - ${salary.salaryTo} ${translations?.agreements?.month}` }}
                                    />
                                );}
                            )}
                        </div>

                        <div className="content-description">
                            <div className="image-container">
                                <img
                                    className="offer-image"
                                    src={job.createSource === 'cbop' ? '/images/jobs/e-praca-logo.svg' : (imageResizer(job.companyLogo?.imageUrl, { width: 200 }) || '/images/jobs/image-placeholder.svg')}
                                    loading="lazy"
                                    alt={job?.companyName}
                                    width="155"
                                    height="163"
                                />
                            </div>
                            <div className="description">
                                <div
                                    className="subtitle"
                                    dangerouslySetInnerHTML={{ __html: job?.companyName }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="desktop-box">
                    <div className="title-row">
                        <div
                            className="title"
                            dangerouslySetInnerHTML={{ __html: job?.name }}
                        />
                        {!job?.isClosed && (
                            <div className="controls">
                                <SaveButton
                                    jobId={job?.id}
                                    cityId={jobCity?.city?.id}
                                    disabled={job?.isClosed}
                                    iconOnly
                                />
                                <ShareButton
                                    route={Routes.PublicJob}
                                    variables={{
                                        slug: job.slug,
                                    }}
                                    disabled={job?.isClosed}
                                    iconOnly
                                />
                            </div>

                        )}
                    </div>
                    <div className="body-row">
                        <div className="image-container">
                            <img
                                className="offer-image"
                                src={job.createSource === 'cbop' ? '/images/jobs/e-praca-logo.svg' : (imageResizer(job.companyLogo?.imageUrl, { width: 200 }) || '/images/jobs/image-placeholder.svg')}
                                loading="lazy"
                                alt={job?.companyName}
                                width="155"
                                height="163"
                            />
                        </div>
                        <div className="content-container">
                        <div className="content-description">
                            <div className="description">
                                { job?.jobSalaries && job?.jobSalaries.map((salary, idx) =>
                                {return translations?.[salary.name]?.label ?
                                    (
                                        <div
                                            key={salary.id}
                                            className={`title title-${idx}`}
                                            dangerouslySetInnerHTML={{ __html: `${translations?.[salary.name]?.label}: ${salary.salaryFrom} - ${salary.salaryTo} ${translations?.[salary.name]?.month}` }}
                                        />
                                    ) : salary.salaryFrom > 0 && (
                                        <div
                                            key={salary.id}
                                            className={`title title-${idx}`}
                                            dangerouslySetInnerHTML={{ __html: `${salary.salaryFrom} - ${salary.salaryTo} ${translations?.agreements?.month}` }}
                                        />
                                    );}
                                )}
                                <div
                                    className="subtitle"
                                    dangerouslySetInnerHTML={{ __html: job?.companyName }}
                                />
                                {job?.companyDescription && (
                                    <div className="description-box">
                                        <div
                                            className="company-description"
                                            dangerouslySetInnerHTML={{ __html: job?.companyDescription }}
                                        />
                                        {job?.company?.companySite?.isPublished && (
                                            <span
                                                className='company-site-button'
                                                onClick={() => router.push(withVariables(Routes.PublicCompanySite, { slug: job?.company?.slug }))}
                                            >
                                                Przejdź do profilu pracodawcy.
                                            </span>
                                        )}
                                    </div>
                                )}
                                {!job?.companyDescription && job?.company?.companySite?.isPublished && (
                                    <div className="description-box-alone">
                                        {job?.company?.companySite?.isPublished && (
                                            <span
                                                className='company-site-button'
                                                onClick={() => router.push(withVariables(Routes.PublicCompanySite, { slug: job?.company?.slug }))}
                                            >
                                            Przejdź do profilu pracodawcy.
                                            </span>
                                        )}
                                    </div>
                                ) }

                            </div>
                            <div>
                                { applicationButtonVisible && !job?.isClosed && (
                                    <Button
                                        {...getButtonProps()}
                                        onClick={() => onApply()}
                                    />
                                )}
                                {job.companyPhone && (
                                    <Button
                                        variant={ButtonVariants.Outline}
                                        layout={ButtonLayouts.Wide}
                                        onClick={handleClick}
                                        element={ButtonElements.Anchor}
                                        className={classnames(
                                            'phone-btn',
                                            { active : isBtnActice }
                                        )}
                                        href={isBtnActice ? `tel:${job?.companyPhone}` : null}
                                        hoverText={prettierPhoneNumber(job?.companyPhone)}
                                        icon={{
                                            type: ButtonIconTypes.Image,
                                            position: ButtonIconPositions.Before,
                                            alt: 'Zadzwoń',
                                            value: '/images/navigation-icons/phone-r.svg',
                                        }}

                                    >
                                        {translations?.phoneBtn?.label}
                                    </Button>
                                )}
                            </div>
                        </div>
                        </div>
                    </div>
                </div>
            )}


        </StyledComponent>
    );
};

export default PublicLayoutJobsMediumJobBox;